import axios from 'axios';
import {
  studentAutoMarkingListURL,
  studentAutoMarkingDetailURL,
  studentAutoMarkingUploadURL,
  studentAutoMarkingPreviewURL,
  studentSetWorkModulesURL,
  studentSetWorkWorksheetsURL,
  studentAutoMarkingEvalPreviewURL,
  studentAutoMarkingHandbookPreviewURL,
  studentAutoMarkingBatchCreateURL,
  studentAutoMarkingBatchDetailURL,
  studentAutoMarkingBatchListURL,
  studentAutoMarkingBatchRetryItemURL,
} from '../constants/ApiEndPoints';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StudentAutoMarkingFilter {
  studentId?: string;
  subjectId?: string;
  moduleId?: string;
  worksheetNumber?: string;
  status?: string;
  page?: number;
  perPage?: number;
  sortField?: string;
  sortDirection?: string;
}

export interface StudentAutoMarkingItem {
  id: number;
  student_id: number;
  subject_id: number;
  module_id: number;
  worksheet_number: number;
  module_name: string;
  module_slug?: string;
  subject_name: string;
  total_marks: string | number | null;
  max_marks: string | number | null;
  percentage: number | null;
  summary?: {
    maxMarks?: number;
    incorrect?: number;
    percentage?: number;
    totalMarks?: number;
    unanswered?: number;
    correctAnswers?: number;
    totalQuestions?: number;
    partiallyCorrect?: number;
  };  created_at: string;
  // kept for backward compat
  student_name?: string;
  status?: string;
  updated_at?: string;
}

export interface StudentAutoMarkingQuestion {
  // camelCase (API response)
  questionNumber?: string;
  questionText?: string;
  studentAnswer?: string;
  correctAnswer?: string;
  marksAwarded?: number;
  maxMarks?: number;
  // snake_case (normalized)
  question_number?: string;
  question_text?: string;
  student_answer?: string;
  correct_answer?: string;
  marks_awarded?: number;
  max_marks?: number;
  status: 'correct' | 'partial' | 'incorrect' | 'unanswered';
  taskType?: string;
  feedback?: string;
}

export interface StudentAutoMarkingSummary {
  // camelCase (API)
  totalQuestions?: number;
  correctAnswers?: number;
  partiallyCorrect?: number;
  totalMarks?: number;
  maxMarks?: number;
  // snake_case (fallback)
  total_questions?: number;
  correct_answers?: number;
  partially_correct?: number;
  total_marks?: number;
  max_marks?: number;
  incorrect: number;
  unanswered: number;
  percentage: number;
}

export interface StudentAutoMarkingDetail extends StudentAutoMarkingItem {
  // camelCase fields from API
  evaluationId?: number;
  subjectName?: string;
  module?: string;
  moduleCode?: string;
  pageNumber?: number;
  handbookPage?: number;
  handbookId?: number;
  teacherSummary?: string;
  createdAt?: string;
  student?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  questionDetails?: StudentAutoMarkingQuestion[];
  // snake_case fallbacks
  questions?: StudentAutoMarkingQuestion[];
  worksheet_file_url?: string;
  answer_key_url?: string;
  summary: StudentAutoMarkingSummary;
}

// Set-work module shape — GET /student/setwork/modules?student_id=&subject_id=
export interface SetWorkModule {
  module_id: number;
  name: string;
  slug: string;
}

// Set-work worksheet shape — GET /student/setwork/worksheets?student_id=&module_id=
export interface SetWorkWorksheet {
  worksheet_number: number;
  assignment_date: string;
}

// ─── API Calls ────────────────────────────────────────────────────────────────

export const getStudentAutoMarkingList = async (filters: StudentAutoMarkingFilter) => {
  try {
    const params = new URLSearchParams();
    if (filters.studentId)      params.append('student_id',       filters.studentId);
    if (filters.subjectId)      params.append('subject_id',       filters.subjectId);
    if (filters.moduleId)       params.append('module_id',        filters.moduleId);
    if (filters.worksheetNumber) params.append('worksheet_number', filters.worksheetNumber);
    if (filters.status)         params.append('status',           filters.status);
    if (filters.page)           params.append('page',             String(filters.page));
    if (filters.perPage)        params.append('perPage',          String(filters.perPage));
    if (filters.sortField)      params.append('sortField',        filters.sortField);
    if (filters.sortDirection)  params.append('sortDirection',    filters.sortDirection);

    const response = await axios.get(`${studentAutoMarkingListURL}?${params.toString()}`);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getStudentAutoMarkingById = async (id: number) => {
  try {
    const response = await axios.get(studentAutoMarkingDetailURL + id);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /student/setwork/modules?student_id=&subject_id=
 * Returns collections[] of SetWorkModule
 */
export const getSetWorkModules = async (studentId: number, subjectId: number) => {
  try {
    const response = await axios.get(
      `${studentSetWorkModulesURL}?student_id=${studentId}&subject_id=${subjectId}`
    );
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /student/setwork/worksheets?student_id=&module_id=
 * Returns collections[] of SetWorkWorksheet { worksheet_number, assignment_date }
 */
export const getSetWorkWorksheets = async (studentId: number, moduleId: number) => {
  try {
    const response = await axios.get(
      `${studentSetWorkWorksheetsURL}?student_id=${studentId}&module_id=${moduleId}`
    );
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const uploadAndAutoMark = async (formData: FormData) => {
  try {
    const response = await axios.post(studentAutoMarkingUploadURL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.status === 200 || response.status === 201) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const getStudentAutoMarkingPreview = async (id: number) => {
  try {
    const response = await axios.get(studentAutoMarkingPreviewURL + id);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /automark/student/setwork/evaluations/:evaluationId/preview
 * Returns { uploadedWorksheetUrl, handbookUrl, handbookType }
 */
export const getEvaluationPreview = async (evaluationId: number) => {
  try {
    const response = await axios.get(`${studentAutoMarkingEvalPreviewURL}${evaluationId}/preview`);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /automark/handbooks/:handbookId/preview?page=:handbookPage
 * Returns { handbookId, page, handbookUrl, handbookType }
 */
export const getHandbookPreview = async (handbookId: number, page: number) => {
  try {
    const response = await axios.get(`${studentAutoMarkingHandbookPreviewURL}${handbookId}/preview?page=${page}`);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

export const validateAutoMarkFile = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Only JPG, JPEG, and PNG image files are allowed.' };
  }
  if (file.size > maxSize) {
    return { valid: false, error: 'File size must not exceed 5MB.' };
  }
  return { valid: true };
};

// ─── Batch Upload Types & Functions ──────────────────────────────────────────

export interface BatchUploadItem {
  id: number;
  original_name: string;
  status: 'queued' | 'processing' | 'done' | 'failed' | 'invalid';
  module_name: string | null;
  module_slug: string | null;
  worksheet_number: number | null;
  evaluation_id: number | null;
  error: string | null;
  total_marks: number | null;
  max_marks: number | null;
  percentage: number | null;
  created_at: string;
  updated_at: string;
}

export interface BatchUploadDetail {
  id: number;
  student_id: number;
  status: 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  total: number;
  done: number;
  error: string | null;
  items: BatchUploadItem[];
  created_at: string;
  updated_at: string;
}

export interface BatchUploadSummary {
  id: number;
  student_id: number;
  status: 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  total: number;
  done: number;
  error: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * POST /automark/student/batch/evaluate
 * Creates a new batch upload with multiple images
 * Returns { success, data: { batch_id, total } }
 */
export const createBatchUpload = async (formData: FormData) => {
  try {
    const response = await axios.post(studentAutoMarkingBatchCreateURL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.status === 200 || response.status === 201) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /automark/student/batch/:id
 * Polls a batch's progress and item details
 * Returns { success, data: BatchUploadDetail } or { success, data: {} } for unknown id
 */
export const getBatchUploadById = async (batchId: number) => {
  try {
    const response = await axios.get(`${studentAutoMarkingBatchDetailURL}${batchId}`);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * GET /automark/student/batches?student_id=X
 * Lists all batches for a student (newest first)
 * Returns { success, collections: BatchUploadSummary[] }
 */
export const listBatchUploads = async (studentId: number) => {
  try {
    const response = await axios.get(`${studentAutoMarkingBatchListURL}?student_id=${studentId}`);
    if (response.status === 200) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * POST /automark/student/batch/:batchId/items/:itemId/retry
 * Retries a failed or queued batch item
 * Returns { success, message, data }
 */
export const retryBatchItem = async (batchId: number, itemId: number) => {
  try {
    const response = await axios.post(`${studentAutoMarkingBatchRetryItemURL}${batchId}/items/${itemId}/retry`);
    if (response.status === 200 || response.status === 201) return response.data;
  } catch (error: any) {
    return error?.response?.data;
  }
};
